import * as Api from '@ViewModels';
import * as React from 'react';
import { toast } from 'sonner';
import { useFullscreenModal } from '../../../../models/hooks/appStateHooks';
import { CampaignSessionManager } from './campaignSessionManager';
import { CampaignResource, campaignSessionStore } from './campaignSessionStore';
import { CONTENT_TYPE_MAP, RESOURCE_ROUTES } from './resourceConfig';
import { getAIContextFromProps, getScheduledDateFromProps, getSourceTypeFromContent } from './resourceHelpers';
import { IAIScheduleSuccessProps, ResourceStatus, ResourceType } from './types';
import { useCampaignSessionStore } from './useCampaignSessionStore';

export interface UseCampaignSessionReturn {
	session: typeof campaignSessionStore.currentSession;
	primaryResource: CampaignResource | null;
	secondaryResources: ResourceStatus[];
	incompleteResources: ResourceType[];
	handleGenerateResource: (targetType: ResourceType, sourceContent?: Api.AIContext) => void;
	handleClose: () => void;
}

export const useCampaignSession = (props: IAIScheduleSuccessProps): UseCampaignSessionReturn => {
	const { resourceType, onClose } = props;
	const fullscreenModal = useFullscreenModal();
	const initializedRef = React.useRef(false);

	React.useEffect(() => {
		// Only initialize once on mount, prevent re-initialization when session is cleared
		if (initializedRef.current) {
			return;
		}

		const initializeCampaignSession = () => {
			try {
				const aiContext = getAIContextFromProps(props);
				if (aiContext) {
					const scheduledDate = getScheduledDateFromProps(props);

					CampaignSessionManager.startSession(resourceType, aiContext, scheduledDate);
					initializedRef.current = true;
				}
			} catch (error) {
				console.error('Error initializing campaign session:', error);
			}
		};

		initializeCampaignSession();
		try {
			campaignSessionStore.clearExpiredSessions();
		} catch (error) {
			console.error('Error clearing expired sessions:', error);
		}
	}, [resourceType, props]);

	const handleGenerateResource = React.useCallback(
		(targetType: ResourceType, providedSourceContent?: Api.AIContext) => {
			try {
				const activeSession = campaignSessionStore.currentSession;
				const primaryResourceType = activeSession?.primaryResource?.type;
				const primaryResourceContent = activeSession?.primaryResource?.content;

				const sourceContent = providedSourceContent || primaryResourceContent || getAIContextFromProps(props);
				const sourceType = getSourceTypeFromContent(sourceContent) || primaryResourceType || resourceType;

				if (!sourceContent) {
					console.error('Error generating resource: No source content available');
					throw new Error('No source content available for AI generation');
				}

				if (!CONTENT_TYPE_MAP[targetType]) {
					console.error('Error generating resource: Invalid target type:', targetType);
					throw new Error(`Invalid target type: ${targetType}`);
				}

				if (!CONTENT_TYPE_MAP[sourceType as ResourceType]) {
					console.error('Error generating resource: Invalid source type:', sourceType);
					throw new Error(`Invalid source type: ${sourceType}`);
				}

				campaignSessionStore.setAISourceData(sourceContent, sourceType as ResourceType, true);

				fullscreenModal.history.push({
					pathname: RESOURCE_ROUTES[targetType],
				});
			} catch (error) {
				console.error('Error generating resource:', error);
				toast.error(`Error generating ${targetType}: ${error instanceof Error ? error.message : 'Unknown error'}`);
			}
		},
		[props, resourceType, fullscreenModal.history]
	);

	const { currentSession, secondaryResources: storeSecondaryResources } = useCampaignSessionStore();
	const session = currentSession;
	const primaryResource = session?.primaryResource || null;

	const handleClose = React.useCallback(() => {
		try {
			campaignSessionStore.clearSession();
		} catch (error) {
			console.error('Error clearing campaign session on close:', error);
			campaignSessionStore.clearExpiredSessions();
		}

		if (onClose) {
			onClose();
		} else {
			fullscreenModal.dismissModal();
		}
	}, [onClose, fullscreenModal]);

	const secondaryResources = storeSecondaryResources;
	const incompleteResources = session ? CampaignSessionManager.getIncompleteResources(session) : [];

	return {
		session,
		primaryResource,
		secondaryResources,
		incompleteResources,
		handleGenerateResource,
		handleClose,
	};
};
